import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";

import "./styles/index.css";

import {
  type AccountInfo,
  type AuthenticationResult,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  type EventMessage,
  EventType,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { I18nextProvider } from "react-i18next";
import App from "./App";
import { getMsalInstance } from "./auth/MsalConfig";
import { clearStaleInteractionStatus } from "./auth/msalStorage";
import i18n from "./i18n";
import { ReactAriaI18nProvider } from "./i18n/ReactAriaI18nProvider";

const msalInstance = getMsalInstance();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

const failureWindowMs = 15_000;
const failureThreshold = 2;
const tokenFailureTimestamps: number[] = [];
let recoveryRedirectInFlight = false;

function recordAcquireFailureAndShouldRecover() {
  const now = Date.now();
  tokenFailureTimestamps.push(now);

  while (tokenFailureTimestamps.length > 0 && now - tokenFailureTimestamps[0] > failureWindowMs) {
    tokenFailureTimestamps.shift();
  }

  return tokenFailureTimestamps.length >= failureThreshold;
}

async function recoverFromTokenFailures(account: AccountInfo | null) {
  if (recoveryRedirectInFlight) {
    return;
  }

  recoveryRedirectInFlight = true;
  clearStaleInteractionStatus(msalInstance.getConfiguration().auth.clientId);

  try {
    await msalInstance.clearCache({ account });
    await msalInstance.acquireTokenRedirect({
      scopes: ["User.Read"],
      ...(account ? { account } : {}),
    });
  } finally {
    recoveryRedirectInFlight = false;
  }
}

// Listen for success events to ensure we capture login in other tabs/windows if needed
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    const account = payload.account;
    msalInstance.setActiveAccount(account);
  }

  if (event.eventType !== EventType.ACQUIRE_TOKEN_FAILURE) {
    return;
  }

  if (
    event.error instanceof BrowserAuthError &&
    event.error.errorCode === BrowserAuthErrorCodes.interactionInProgress
  ) {
    return;
  }

  const shouldRecover = recordAcquireFailureAndShouldRecover();
  if (!shouldRecover) {
    return;
  }

  const account =
    (event.payload as AuthenticationResult | null)?.account ??
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0] ??
    null;

  void recoverFromTokenFailures(account);
});

// Initialize MSAL and handle any redirects before rendering
msalInstance
  .initialize()
  .then(() => {
    console.log("[MSAL] Initialized");
    // Handle redirect promise to ensure hash is processed
    // This is critical for popup and redirect flows
    return msalInstance.handleRedirectPromise();
  })
  .then((tokenResponse) => {
    console.log("[MSAL] HandleRedirectPromise completed", tokenResponse);

    if (!tokenResponse) {
      clearStaleInteractionStatus(msalInstance.getConfiguration().auth.clientId);
    }

    // If running inside a popup or iframe (from ssoSilent), we should not render the full app
    // after the hash has been consumed. MSAL will signal the main window and close/resolve.
    if (window.opener || window.parent !== window) {
      console.log("[MSAL] In popup/iframe, stopping render");

      // If we processed a token, or even if we didn't but are in a popup,
      // we generally don't want to spin up the whole React app which might interfere.
      // However, if handleRedirectPromise didn't close the window (which it should for popup),
      // we might just log and stop.
      return;
    }

    // Set active account if we have a token response (e.g. from redirect flow)
    if (tokenResponse?.account) {
      msalInstance.setActiveAccount(tokenResponse.account);
    } else {
      // If no token response, but we have accounts, ensure active account is set
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }

    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <I18nextProvider i18n={i18n}>
          <MsalProvider instance={msalInstance}>
            <ReactAriaI18nProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ReactAriaI18nProvider>
          </MsalProvider>
        </I18nextProvider>
      </React.StrictMode>,
    );
  })
  .catch((error) => {
    console.error("MSAL Initialization Failed", error);
    // Render error state — renders before <I18nextProvider> mounts, so this
    // file is listed in EXEMPT_FILES in scripts/check-i18n-hardcoded-strings.ts.
    // Adding another top-level bootstrap fallback like this? Add its path there too.
    ReactDOM.createRoot(rootElement).render(
      <div style={{ padding: 20 }}>
        <h1>Authentication Initialization Failed</h1>
        <pre>{error.toString()}</pre>
      </div>,
    );
  });
